/**
 * V11.7 — Reporting Palmarès Multi-Disciplines (COPF / Admin).
 *
 * Dashboard filtrable :
 *  - Discipline / Famille / Année / Médaille / Niveau / Fédération
 *  - Flags : Titre majeur, Résultat collectif
 *
 * Affiche :
 *  - KPIs (total résultats, athlètes touchés, or/argent/bronze, titres majeurs)
 *  - Distribution par famille / niveau / année / fédération
 *  - Top athlètes (score pondéré)
 *  - Liste d'entrées filtrées (échantillon)
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Trophy, Medal, Users, Star, Filter, Flag, Loader2, TrendingUp, X, BarChart3,
} from "lucide-react";
import { showError } from "@/lib/errors";
import { useAuth } from "@/lib/auth";

const ALL = "__all__";
const FAMILY_LABELS = {
  combat: "Combat", team: "Collectif", timed: "Chronométré", vaa: "Va'a",
  strength: "Force", score_judge: "Score/Jugement", racket: "Raquette",
  shooting: "Tir", generic: "Générique",
};
const LEVELS = [
  { v: "local", l: "Local" }, { v: "territorial", l: "Territorial" },
  { v: "national", l: "National" }, { v: "regional", l: "Régional" },
  { v: "oceanien", l: "Océanien" }, { v: "continental", l: "Continental" },
  { v: "international", l: "International" }, { v: "mondial", l: "Mondial" },
  { v: "jeux-du-pacifique", l: "Jeux du Pacifique" }, { v: "olympique", l: "Olympique" },
];
const MEDALS = [
  { v: "or", l: "Or" }, { v: "argent", l: "Argent" }, { v: "bronze", l: "Bronze" },
  { v: "finaliste", l: "Finaliste" }, { v: "podium", l: "Podium" },
];

function KpiTile({ label, value, icon: Icon, tone = "neutral", testid }) {
  const tones = {
    gold: "border-[hsl(var(--aito-gold))]/40 bg-[hsl(var(--aito-gold))]/5 text-[hsl(var(--aito-gold))]",
    silver: "border-slate-400/40 bg-slate-100/50 text-slate-700 dark:text-slate-300",
    bronze: "border-orange-400/40 bg-orange-100/50 text-orange-700 dark:text-orange-300",
    lagoon: "border-[hsl(var(--aito-lagoon))]/40 bg-[hsl(var(--aito-lagoon))]/5 text-[hsl(var(--aito-lagoon))]",
    neutral: "border-muted/40",
  };
  return (
    <Card className={`p-4 flex items-start gap-3 ${tones[tone]}`} data-testid={testid}>
      {Icon && <Icon className="h-5 w-5 shrink-0 mt-0.5" />}
      <div>
        <div className="text-2xl font-display font-bold">{value ?? "—"}</div>
        <div className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</div>
      </div>
    </Card>
  );
}

function DistributionCard({ title, data, total, icon: Icon, testid, labelMap = {} }) {
  const items = Object.entries(data || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);
  if (items.length === 0) return null;
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />}
        <h3 className="font-display font-semibold text-sm">{title}</h3>
      </div>
      <div className="space-y-1.5">
        {items.map(([key, count]) => {
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          const label = labelMap[key] || key;
          return (
            <div key={key} className="text-xs">
              <div className="flex justify-between mb-0.5">
                <span className="capitalize truncate max-w-[70%]" title={label}>{label}</span>
                <span className="font-mono text-muted-foreground">{count} · {pct}%</span>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-[hsl(var(--aito-lagoon))]"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default function CopfReportingPalmaresPage() {
  const { user } = useAuth();
  const isStaff = (user?.roles || []).some((r) => ["copf", "admin"].includes(r));

  const [disciplines, setDisciplines] = useState([]);
  const [federations, setFederations] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filtres
  const [f, setF] = useState({
    discipline: ALL,
    family: ALL,
    year: ALL,
    medal: ALL,
    level: ALL,
    federation: ALL,
    is_major_title: false,
    is_collective: false,
  });

  useEffect(() => {
    api.get("/disciplines").then((r) => setDisciplines(r.data.items || [])).catch(() => {});
    api.get("/federations?limit=200").then((r) => setFederations(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isStaff) return;
    setLoading(true);
    const params = {};
    if (f.discipline !== ALL) params.discipline = f.discipline;
    if (f.family !== ALL) params.family = f.family;
    if (f.year !== ALL) params.year = f.year;
    if (f.medal !== ALL) params.medal = f.medal;
    if (f.level !== ALL) params.level = f.level;
    if (f.federation !== ALL) params.federation = f.federation;
    if (f.is_major_title) params.is_major_title = true;
    if (f.is_collective) params.is_collective = true;

    api.get("/copf/reporting/palmares", { params })
      .then((r) => setData(r.data))
      .catch((e) => { setData(null); showError(e, { prefix: "Reporting :" }); })
      .finally(() => setLoading(false));
  }, [isStaff, f]);

  const upd = (patch) => setF((c) => ({ ...c, ...patch }));
  const clearAll = () => setF({
    discipline: ALL, family: ALL, year: ALL, medal: ALL, level: ALL, federation: ALL,
    is_major_title: false, is_collective: false,
  });

  const years = useMemo(() => {
    const cur = new Date().getUTCFullYear();
    return [cur, cur - 1, cur - 2, cur - 3, cur - 4, cur - 5, cur - 6];
  }, []);

  const disciplineLabelMap = useMemo(() => Object.fromEntries(disciplines.map((d) => [d.slug, d.name])), [disciplines]);

  if (!user) {
    return <div className="mx-auto max-w-3xl px-4 py-16 text-center">
      <Trophy className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
      <p>Cette page est réservée aux membres COPF et Admin.</p>
      <Link to="/connexion" className="text-sm text-[hsl(var(--aito-lagoon))] hover:underline mt-2 inline-block">Se connecter</Link>
    </div>;
  }
  if (!isStaff) {
    return <div className="mx-auto max-w-3xl px-4 py-16 text-center">
      <p>Accès réservé aux rôles <strong>COPF</strong> ou <strong>Admin</strong>.</p>
    </div>;
  }

  const kpis = data?.kpis || {};
  const total = kpis.total_achievements || 0;
  const dist = data?.distribution || {};

  return (
    <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-8" data-testid="copf-reporting-page">
      <header className="mb-6">
        <h1 className="font-display text-3xl sm:text-4xl font-bold flex items-center gap-2">
          <BarChart3 className="h-8 w-8 text-[hsl(var(--aito-lagoon))]" />
          Reporting Palmarès Multi-Sports
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Pilotage transversal du sport polynésien — filtre par discipline, famille, fédération, année, médaille.
        </p>
      </header>

      {/* Filtres */}
      <Card className="p-4 mb-6 space-y-3" data-testid="copf-reporting-filters">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
          <span className="text-sm font-medium">Filtres</span>
          <Button variant="ghost" size="sm" onClick={clearAll} className="ml-auto text-xs h-7" data-testid="copf-reporting-clear">
            <X className="h-3 w-3 mr-1" /> Réinitialiser
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          <Select value={f.discipline} onValueChange={(v) => upd({ discipline: v })}>
            <SelectTrigger data-testid="copf-filter-discipline"><SelectValue placeholder="Discipline" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value={ALL}>Toutes disciplines</SelectItem>
              {disciplines.map((d) => <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={f.family} onValueChange={(v) => upd({ family: v })}>
            <SelectTrigger data-testid="copf-filter-family"><SelectValue placeholder="Famille" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Toutes familles</SelectItem>
              {Object.entries(FAMILY_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(f.year)} onValueChange={(v) => upd({ year: v === ALL ? ALL : parseInt(v) })}>
            <SelectTrigger data-testid="copf-filter-year"><SelectValue placeholder="Année" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Toutes années</SelectItem>
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={f.medal} onValueChange={(v) => upd({ medal: v })}>
            <SelectTrigger data-testid="copf-filter-medal"><SelectValue placeholder="Médaille" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Toutes médailles</SelectItem>
              {MEDALS.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={f.level} onValueChange={(v) => upd({ level: v })}>
            <SelectTrigger data-testid="copf-filter-level"><SelectValue placeholder="Niveau" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous niveaux</SelectItem>
              {LEVELS.map((l) => <SelectItem key={l.v} value={l.v}>{l.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={f.federation} onValueChange={(v) => upd({ federation: v })}>
            <SelectTrigger data-testid="copf-filter-federation"><SelectValue placeholder="Fédération" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value={ALL}>Toutes fédés</SelectItem>
              {federations.map((fed) => <SelectItem key={fed.id || fed.slug} value={fed.name || fed.slug}>{fed.name || fed.slug}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-4 pt-1">
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <Checkbox checked={f.is_major_title} onCheckedChange={(v) => upd({ is_major_title: !!v })} data-testid="copf-filter-major" />
            <Star className="h-3 w-3 text-[hsl(var(--aito-gold))]" /> Titres majeurs uniquement
          </label>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <Checkbox checked={f.is_collective} onCheckedChange={(v) => upd({ is_collective: !!v })} data-testid="copf-filter-collective" />
            <Users className="h-3 w-3" /> Résultats collectifs uniquement
          </label>
        </div>
      </Card>

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <div className="grid md:grid-cols-3 gap-3">
            <Skeleton className="h-40" /><Skeleton className="h-40" /><Skeleton className="h-40" />
          </div>
        </div>
      ) : !data ? (
        <Card className="p-8 text-center text-muted-foreground">
          <TrendingUp className="h-10 w-10 mx-auto mb-2" />
          Impossible de charger le reporting. Réessayez.
        </Card>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="copf-reporting-kpis">
            <KpiTile label="Résultats" value={kpis.total_achievements} icon={Trophy} tone="lagoon" testid="kpi-total" />
            <KpiTile label="Athlètes touchés" value={kpis.total_athletes_touched} icon={Users} testid="kpi-athletes" />
            <KpiTile label="Or" value={kpis.medal_gold} icon={Medal} tone="gold" testid="kpi-gold" />
            <KpiTile label="Argent" value={kpis.medal_silver} icon={Medal} tone="silver" testid="kpi-silver" />
            <KpiTile label="Bronze" value={kpis.medal_bronze} icon={Medal} tone="bronze" testid="kpi-bronze" />
            <KpiTile label="Titres majeurs" value={kpis.total_major_titles} icon={Star} tone="gold" testid="kpi-major" />
            <KpiTile label="Collectifs" value={kpis.total_collective} icon={Users} testid="kpi-collective" />
          </div>

          {/* Distributions */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <DistributionCard title="Par famille sportive" data={dist.by_family} total={total} icon={Trophy} labelMap={FAMILY_LABELS} testid="dist-family" />
            <DistributionCard title="Par discipline" data={dist.by_discipline} total={total} icon={Trophy} labelMap={disciplineLabelMap} testid="dist-discipline" />
            <DistributionCard title="Par niveau" data={dist.by_level} total={total} icon={TrendingUp} testid="dist-level" />
            <DistributionCard title="Par année" data={dist.by_year} total={total} icon={TrendingUp} testid="dist-year" />
            <DistributionCard title="Par fédération" data={dist.by_federation} total={total} icon={Flag} testid="dist-federation" />
            <DistributionCard title="Par médaille" data={dist.by_medal} total={total} icon={Medal} testid="dist-medal" />
          </div>

          {/* Top athlètes */}
          {data.top_athletes?.length > 0 && (
            <Card className="p-4 mb-6" data-testid="copf-reporting-top-athletes">
              <div className="flex items-center gap-2 mb-3">
                <Star className="h-4 w-4 text-[hsl(var(--aito-gold))]" />
                <h3 className="font-display font-semibold text-sm">Top athlètes (score pondéré médailles + titres majeurs)</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {data.top_athletes.map((a, i) => (
                  <Link key={a.slug || a.id} to={`/athletes/${a.slug}`} className="p-2 rounded border hover:bg-muted transition text-xs" data-testid={`top-athlete-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-muted-foreground">#{i + 1}</span>
                      <Badge variant="outline" className="text-[9px]">{a.score} pts</Badge>
                    </div>
                    <div className="font-medium truncate mt-1">{a.name}</div>
                  </Link>
                ))}
              </div>
            </Card>
          )}

          {/* Sample entries */}
          {data.entries_sample?.length > 0 && (
            <Card className="p-4" data-testid="copf-reporting-entries">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-semibold text-sm">Résultats détaillés ({data.entries_returned})</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="copf-reporting-entries-table">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-2 py-2 text-left">Athlète</th>
                      <th className="px-2 py-2 text-left">Discipline</th>
                      <th className="px-2 py-2 text-left">Titre</th>
                      <th className="px-2 py-2 text-left">Détails</th>
                      <th className="px-2 py-2 text-left w-16">Année</th>
                      <th className="px-2 py-2 text-left">Niveau</th>
                      <th className="px-2 py-2 text-left">Médaille</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.entries_sample.map((e, i) => (
                      <tr key={i} className="border-t hover:bg-muted/30" data-testid={`copf-entry-${i}`}>
                        <td className="px-2 py-1.5">
                          <Link to={`/athletes/${e.athlete_slug}`} className="hover:underline">
                            {e.athlete_name}
                          </Link>
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="flex items-center gap-1 flex-wrap">
                            <span className="capitalize">{disciplineLabelMap[e.discipline] || e.discipline}</span>
                            {e.sport_family && <Badge variant="outline" className="text-[9px]">{FAMILY_LABELS[e.sport_family]}</Badge>}
                          </div>
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="flex items-center gap-1">
                            {e.is_major_title && <Star className="h-3 w-3 text-[hsl(var(--aito-gold))]" />}
                            {e.is_collective && <Users className="h-3 w-3 text-muted-foreground" />}
                            <span className="truncate max-w-[300px]" title={e.title}>{e.title}</span>
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-muted-foreground">
                          {[e.team_name, e.crew_format, e.distance, e.time_performance, e.weight, e.category].filter(Boolean).join(" · ")}
                        </td>
                        <td className="px-2 py-1.5">{e.year || "—"}</td>
                        <td className="px-2 py-1.5 capitalize">{e.level?.replace(/-/g, " ") || "—"}</td>
                        <td className="px-2 py-1.5">
                          {e.medal ? (
                            <Badge className={
                              e.medal === "or" ? "bg-[hsl(var(--aito-gold))] text-black" :
                              e.medal === "argent" ? "bg-slate-300 text-black" :
                              e.medal === "bronze" ? "bg-orange-300 text-black" : ""
                            }>{e.medal}</Badge>
                          ) : e.rank ? <span className="text-muted-foreground">{e.rank}<sup>e</sup></span> : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
